import { Building2, FileWarning, RefreshCw } from 'lucide-react';
import type { ReactElement, ReactNode } from 'react';

import { BackLink } from '@/components/layout/BackLink';
import { TO_PANEL } from '@/lib/back-nav';

import { GatedChrome, RestrictedPanel } from '@/components/auth/GatedChrome';
import { PermissionErrorPanel } from '@/components/auth/PermissionErrorPanel';
import { usePermissionState } from '@/hooks/usePermission';
import { useActiveCompany } from '@/lib/active-company-context';

import { EventTable } from './components/EventTable';
import { TraceEmpty } from './components/TraceEmpty';
import { TraceabilitySkeleton } from './components/TraceabilitySkeleton';
import { TraceFilterBar } from './components/TraceFilterBar';
import { TraceKpis } from './components/TraceKpis';
import type { AuditLogRow } from './types';
import { exportSearch, useAuditLog } from './use-audit-log';
import { useAuditSummary } from './use-audit-summary';
import { isFiltered, toQueryFilters, useTraceFilters } from './use-url-filters';
import { ExportMenu } from '@/components/ui/ExportMenu';
import { InlineError, StatePanel } from '@/components/ui/StatePanel';
import { errorAdvice, refreshAdvice } from '@/lib/error-advice';

const BREADCRUMB = [
  { label: 'QMS', to: '/dashboard', strong: true },
  { label: 'Configuración' },
  { label: 'Trazabilidad', strong: true },
];

function Frame({ children }: { children: ReactNode }): ReactElement {
  // G4: sin permiso confirmado, las migas y el encabezado no nombran la pantalla.
  const perm = usePermissionState('system:audit:read');
  return (
    <>
      <GatedChrome state={perm.state} breadcrumb={BREADCRUMB} navContext="Trazabilidad" />
      <BackLink target={TO_PANEL} />
      {children}
    </>
  );
}

export function Traceability(): ReactElement {
  const perm = usePermissionState('system:audit:read');
  const canRead = perm.state === 'granted';
  const { activeCompanyId, isLoading: companyLoading } = useActiveCompany();
  const { filters, setSegment, setRange, clearAll } = useTraceFilters();

  const query = useAuditLog(toQueryFilters(filters), {
    companyId: activeCompanyId,
    enabled: canRead && Boolean(activeCompanyId),
  });
  const summary = useAuditSummary({
    companyId: activeCompanyId,
    enabled: canRead && Boolean(activeCompanyId),
  });

  if (perm.state === 'error') {
    return (
      <Frame>
        <PermissionErrorPanel error={perm.error} onRetry={perm.retry} retrying={perm.retrying} />
      </Frame>
    );
  }
  if (perm.state === 'denied') {
    return (
      <Frame>
        <RestrictedPanel />
      </Frame>
    );
  }

  // Con permiso pero sin empresa activa (usuario sin membresía, o cross-company
  // sin empresa seleccionada) la consulta no puede dispararse: mostramos un estado
  // claro en vez de un skeleton infinito (UX-FLOW-01 / UX-ERR-01).
  if (canRead && !companyLoading && !activeCompanyId) {
    return (
      <Frame>
        <StatePanel icon={<Building2 />} title="Selecciona una empresa activa" role="status">
          Elige una empresa activa en la barra superior.
        </StatePanel>
      </Frame>
    );
  }

  const rows = query.data?.pages.flatMap((p) => p.data) ?? [];
  // El total del mes solo tiene sentido en la vista sin filtrar; al filtrar por
  // categoría o fecha, "N de M" mezclaría un conteo filtrado con uno sin filtrar.
  const hasFilters = isFiltered(filters);

  return (
    <Frame>
      <div className="pagehead rise">
        <div className="grow">
          <h1 style={{ marginBottom: 4 }}>Trazabilidad</h1>
          <p style={{ maxWidth: '70ch' }}>Acciones registradas en la empresa activa.</p>
        </div>
        {canRead && activeCompanyId && (
          <div className="pagehead-actions">
            {/* Exportación sellada (spec 2026-10-14 §3 D4): lo filtrado completo. */}
            <ExportMenu
              path="/api/v1/audit-logs/export"
              query={exportSearch(toQueryFilters(filters))}
              title="Trazabilidad"
            />
          </div>
        )}
      </div>

      <div style={{ marginTop: 16 }}>
        <TraceKpis
          data={summary.data}
          isLoading={!canRead || summary.isLoading || !activeCompanyId}
          error={
            summary.isError
              ? {
                  onRetry: () => void summary.refetch(),
                  retrying: summary.isFetching,
                  cause: summary.error,
                }
              : null
          }
        />
      </div>

      <TraceFilterBar
        className="rise d1"
        style={{ marginTop: 16 }}
        filters={filters}
        onSegment={setSegment}
        onRange={setRange}
      />

      <TraceabilityBody
        isLoading={!canRead || companyLoading || query.isLoading}
        isError={query.isError}
        error={query.error}
        rows={rows}
        monthTotal={hasFilters ? null : (summary.data?.month ?? null)}
        hasNextPage={query.hasNextPage}
        isFetchingNextPage={query.isFetchingNextPage}
        onMore={() => void query.fetchNextPage()}
        onRetry={() => void query.refetch()}
        empty={<TraceEmpty filtered={hasFilters} onClearFilters={clearAll} />}
      />
    </Frame>
  );
}

interface BodyProps {
  isLoading: boolean;
  isError: boolean;
  /** La falla de la consulta: decide qué se le aconseja al usuario. */
  error: unknown;
  rows: AuditLogRow[];
  monthTotal: number | null;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  onMore: () => void;
  onRetry: () => void;
  /** Estado sin filas (QA F1 #9). */
  empty: ReactElement;
}

function TraceabilityBody({
  isLoading,
  isError,
  error,
  rows,
  monthTotal,
  hasNextPage,
  isFetchingNextPage,
  onMore,
  onRetry,
  empty,
}: BodyProps): ReactElement {
  if (isLoading) {
    return <TraceabilitySkeleton />;
  }
  if (isError && rows.length === 0) {
    return (
      <StatePanel
        icon={<FileWarning />}
        title="No se pudo cargar la bitácora"
        tone="danger"
        role="alert"
        onRetry={onRetry}
        className="d2"
      >
        {errorAdvice(error)}
      </StatePanel>
    );
  }
  if (rows.length === 0) {
    return empty;
  }
  return (
    <div className="card rise d2">
      <div className="ch">
        <h3>Bitácora de eventos</h3>
        <span className="count">del más reciente al más antiguo</span>
      </div>
      {/* Una recarga que falla no reemplaza lo que ya se veía (Lente B del lote). */}
      {isError && (
        <InlineError icon={<RefreshCw size={16} />} onRetry={onRetry}>
          No se pudo actualizar la bitácora. Se muestran los eventos anteriores.
          {refreshAdvice(error)}
        </InlineError>
      )}
      <div className="t-scroll">
        <EventTable rows={rows} />
      </div>
      <div className="pagebar">
        <span>
          {monthTotal !== null
            ? `Mostrando ${rows.length.toLocaleString('es-CR')} de ${monthTotal.toLocaleString('es-CR')} asientos del mes`
            : `${rows.length} ${rows.length === 1 ? 'asiento' : 'asientos'}`}
        </span>
        {hasNextPage && (
          <button
            type="button"
            className="btn mut sm"
            onClick={onMore}
            disabled={isFetchingNextPage}
          >
            {isFetchingNextPage ? 'Cargando…' : 'Cargar más'}
          </button>
        )}
      </div>
    </div>
  );
}
